import { useState } from "react";
import {
  Button,
  Checkbox,
  ColorPicker,
  DatePicker,
  Field,
  FileDrop,
  Input,
  NumberInput,
  PasswordInput,
  PinInput,
  Radio,
  RadioGroup,
  SearchInput,
  SegmentedControl,
  Select,
  Slider,
  Switch,
  TagsInput,
  Textarea,
  TimePicker,
} from "@one-nexty/components-web";

import { CodePane, Preview } from "../ui/preview";
import { Section, SubSection } from "../ui/section";
import { ChartIcon, UsersIcon } from "../ui/icons";

export function FormsSection() {
  const [email, setEmail] = useState("pas-un-email");
  const emailError = email.includes("@") ? undefined : "Adresse e-mail invalide.";

  return (
    <Section
      id="formulaires"
      eyebrow="Formulaires"
      title="Champs de saisie"
      description="Un champ rempli, pas un champ bordé : c'est le fond qui identifie la zone de saisie, et il remonte d'un cran de surface au focus."
    >
      <SubSection title="Texte, e-mail, mot de passe">
        <Preview
          layout="stack"
          className="max-w-md"
          label="form.tsx"
          code={`<Field label="Nom du projet" hint="Visible par toute l'équipe." required>
  <Input placeholder="Refonte du portail" />
</Field>

<Field label="E-mail" error={emailError}>
  <Input type="email" value={email} onChange={onChange} />
</Field>

<Field label="Mot de passe" hint="12 caractères minimum.">
  <PasswordInput />
</Field>

<Field label="Description">
  <Textarea autoResize />
</Field>`}
        >
          <Field label="Nom du projet" hint="Visible par toute l'équipe." required>
            <Input placeholder="Refonte du portail" />
          </Field>

          <Field label="E-mail" error={emailError}>
            <Input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="prenom@exemple.fr"
            />
          </Field>

          <Field label="Mot de passe" hint="12 caractères minimum.">
            <PasswordInput defaultValue="correcthorsebatterystaple" />
          </Field>

          <Field label="Description" hint="La zone grandit avec le texte.">
            <Textarea autoResize placeholder="À quoi sert ce projet ?" />
          </Field>

          <Field label="Champ désactivé">
            <Input disabled value="Non modifiable" readOnly />
          </Field>
        </Preview>
      </SubSection>

      <SubSection title="Recherche">
        <Preview
          layout="stack"
          className="max-w-md"
          label="search.tsx"
          code={`<SearchInput placeholder="Rechercher…" shortcut={["⌘", "K"]} />`}
        >
          <SearchInput placeholder="Rechercher un composant…" shortcut={["⌘", "K"]} />
          <SearchInput defaultValue="bouton" placeholder="Rechercher…" />
        </Preview>
      </SubSection>

      <SubSection title="Nombres, dates, heures, couleurs">
        <Preview
          layout="stack"
          className="max-w-md"
          label="pickers.tsx"
          code={`<NumberInput defaultValue={2400} step={100} min={0} unit="€" />
<DatePicker defaultValue="2026-09-01" />
<TimePicker defaultValue="14:30" />
<ColorPicker defaultValue="#635BFF" />

<Slider
  defaultValue={65}
  showValue
  formatValue={(value) => \`\${value} %\`}
  marks={[{ value: 0, label: "0" }, { value: 100, label: "100" }]}
/>`}
        >
          <Field label="Quantité">
            <NumberInput defaultValue={3} min={0} max={20} />
          </Field>

          <Field label="Budget mensuel">
            <NumberInput defaultValue={2400} step={100} min={0} unit="€" />
          </Field>

          <Field label="Date de lancement">
            <DatePicker defaultValue="2026-09-01" />
          </Field>

          <Field label="Créneau">
            <TimePicker defaultValue="14:30" />
          </Field>

          <Field label="Couleur d'accentuation">
            <ColorPicker defaultValue="#635BFF" />
          </Field>

          <Field label="Niveau de compression">
            <Slider
              defaultValue={65}
              showValue
              formatValue={(value) => `${value} %`}
              marks={[
                { value: 0, label: "0" },
                { value: 50, label: "50" },
                { value: 100, label: "100" },
              ]}
            />
          </Field>
        </Preview>
      </SubSection>

      <SubSection title="Choix" hint="Segmenté sous 5 options, liste déroulante au-delà">
        <Preview
          layout="stack"
          className="max-w-md"
          label="choices.tsx"
          code={`<SegmentedControl
  aria-label="Période"
  defaultValue="semaine"
  options={[
    { value: "jour", label: "Jour" },
    { value: "semaine", label: "Semaine" },
    { value: "an", label: "Année", disabled: true },
  ]}
/>

<Select placeholder="Choisir une formule…">
  <option value="free">Gratuite</option>
  <option value="team">Équipe</option>
</Select>`}
        >
          <Field label="Période">
            <SegmentedControl
              aria-label="Période"
              defaultValue="semaine"
              options={[
                { value: "jour", label: "Jour" },
                { value: "semaine", label: "Semaine" },
                { value: "mois", label: "Mois" },
                { value: "an", label: "Année", disabled: true },
              ]}
            />
          </Field>

          <Field label="Affichage">
            <SegmentedControl
              aria-label="Mode d'affichage"
              size="sm"
              defaultValue="graph"
              options={[
                { value: "graph", label: "Graphique", icon: <ChartIcon /> },
                { value: "equipe", label: "Équipe", icon: <UsersIcon /> },
              ]}
            />
          </Field>

          <Field label="Formule">
            <Select placeholder="Choisir une formule…">
              <option value="free">Gratuite</option>
              <option value="team">Équipe</option>
              <option value="enterprise">Entreprise</option>
            </Select>
          </Field>
        </Preview>
      </SubSection>

      <SubSection title="Jetons & code à usage unique">
        <Preview
          layout="stack"
          className="max-w-md"
          label="tokens.tsx"
          code={`<TagsInput defaultValue={["design system", "tailwind"]} max={6} />

<PinInput length={6} defaultValue="428" />`}
        >
          <Field label="Mots-clés" hint="Entrée ou virgule pour valider.">
            <TagsInput defaultValue={["design system", "tailwind"]} max={6} />
          </Field>

          <Field label="Code de vérification" hint="Le collage se répartit sur les cases.">
            <PinInput length={6} defaultValue="428" />
          </Field>
        </Preview>
      </SubSection>

      <SubSection title="Fichiers">
        <Preview
          layout="stack"
          className="max-w-md"
          label="upload.tsx"
          code={`<FileDrop multiple maxSize={2 * 1024 * 1024} hint="PNG, JPG ou PDF — 2 Mo maximum" />`}
        >
          <FileDrop multiple maxSize={2 * 1024 * 1024} hint="PNG, JPG ou PDF — 2 Mo maximum" />
        </Preview>
      </SubSection>

      <SubSection title="Cases, radios, interrupteurs">
        <Preview
          layout="stack"
          className="max-w-md"
          label="toggles.tsx"
          code={`<Checkbox label="Recevoir la lettre d'information" defaultChecked />
<Checkbox label="Notifications" description="Un résumé quotidien." />

<RadioGroup legend="Fréquence de facturation">
  <Radio name="billing" value="monthly" label="Mensuelle" defaultChecked />
  <Radio name="billing" value="yearly" label="Annuelle" description="Deux mois offerts." />
</RadioGroup>

<Switch label="Notifications" defaultChecked />`}
        >
          <div className="flex flex-col gap-3">
            <Checkbox label="Recevoir la lettre d'information" defaultChecked />
            <Checkbox
              label="Activer les notifications par e-mail"
              description="Un résumé quotidien, jamais plus."
            />
            <Checkbox label="Option indisponible" disabled />
          </div>

          <RadioGroup legend="Fréquence de facturation">
            <Radio name="billing" value="monthly" label="Mensuelle" defaultChecked />
            <Radio name="billing" value="yearly" label="Annuelle" description="Deux mois offerts." />
          </RadioGroup>

          <Switch
            label="Notifications"
            description="Alertes en temps réel dans le navigateur."
            defaultChecked
          />
          <Switch label="Mode compact" size="sm" />
        </Preview>
      </SubSection>

      <SubSection title="Barre de filtres" hint="Hauteurs alignées sur celles du Button">
        <Preview
          label="filters.tsx"
          code={`<SearchInput placeholder="Filtrer…" className="max-w-56" />
<Select defaultValue="all" className="max-w-40">
  <option value="all">Tous les statuts</option>
</Select>
<Button variant="outline">Réinitialiser</Button>
<Button>Appliquer</Button>`}
        >
          <SearchInput placeholder="Filtrer…" className="max-w-56" />
          <Select defaultValue="all" className="max-w-40">
            <option value="all">Tous les statuts</option>
            <option value="active">Actifs</option>
          </Select>
          <Button variant="outline">Réinitialiser</Button>
          <Button>Appliquer</Button>
        </Preview>
      </SubSection>

      <SubSection title="Accessibilité" hint="Field câble les attributs, vous n'écrivez aucun id">
        <div className="overflow-hidden rounded-xl border border-border bg-surface-raised">
          <CodePane
            label="signup-form.tsx"
            code={`<Field label="Mot de passe" hint="12 caractères minimum" error={errors.password} required>
  <PasswordInput value={password} onChange={onChange} />
</Field>

// Généré par Field :
//   <label for="…-control">     le clic sur le libellé focalise le champ
//   aria-describedby="…-error"  l'erreur est lue avec le champ
//   aria-invalid="true"         l'état visuel suit l'état accessible
//   role="alert"                le message est annoncé dès son apparition`}
          />
        </div>
      </SubSection>
    </Section>
  );
}
